import { throttle } from 'lodash-es';
import { useLayoutEffect } from 'react';
const DEFAULT_THRESHOLD = 300;
const DEFAULT_REENABLE_THRESHOLD = 20;
const map = new WeakMap();
const subscriptions = new WeakMap();
function register(el) {
    const count = (map.get(el) ?? 0) + 1;
    map.set(el, count);
    return () => {
        const next = (map.get(el) ?? 1) - 1;
        if (next === 0) {
            map.delete(el);
            subscriptions.get(el)?.();
            subscriptions.delete(el);
        }
        else {
            map.set(el, next);
        }
    };
}
function subscribe(el, thresholdPx, behavior) {
    let userDidScroll = false;
    // 10ms throttle should help prevent excessive scrolls, while being responsive enough
    // that jitter should not be noticeable.
    const checkAndScroll = throttle(() => {
        const { scrollTop, scrollHeight, clientHeight } = el;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        if (!userDidScroll && distanceFromBottom <= thresholdPx) {
            el.scrollTo({ top: scrollHeight, behavior });
        }
    }, 10);
    const unobserve = observeUserScroll(el, {
        onScrollUp: () => {
            userDidScroll = true;
        },
        onScrollDown: (dist) => {
            if (dist <= DEFAULT_REENABLE_THRESHOLD) {
                userDidScroll = false;
            }
        },
    });
    // MutationObserver with `attributes` catches react-spring's direct
    // style.height mutations (fires as microtask after the DOM write,
    // guaranteeing we read the updated scrollHeight — unlike RAF which
    // has no guaranteed ordering relative to react-spring's RAF).
    const observer = new MutationObserver(checkAndScroll);
    observer.observe(el, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style'],
    });
    const reset = () => {
        userDidScroll = false;
        observer.disconnect();
        unobserve();
        checkAndScroll.cancel();
    };
    return reset;
}
export function observeUserScroll(el, { onScrollUp, onScrollDown, scrollTarget = el, shouldIgnoreScroll, trackViewportResize = false, }) {
    let lastTouchY;
    let lastScrollTop = el.scrollTop;
    let lastScrollHeight = el.scrollHeight;
    let lastClientHeight = el.clientHeight;
    const getDistances = () => {
        const { scrollTop, scrollHeight, clientHeight } = el;
        return {
            fromTop: scrollTop,
            fromBottom: scrollHeight - scrollTop - clientHeight,
        };
    };
    const onWheel = (e) => {
        if (!el.scrollHeight)
            return;
        const d = getDistances();
        if (e.deltaY < 0)
            onScrollUp(d.fromTop);
        else if (e.deltaY > 0)
            onScrollDown(d.fromBottom);
    };
    const onTouchStart = (e) => {
        const touch = e.touches[0];
        if (touch)
            lastTouchY = touch.clientY;
    };
    // Finger dragging down = content scrolling up, and vice versa.
    const onTouchMove = (e) => {
        if (!el.scrollHeight)
            return;
        const touch = e.touches[0];
        if (lastTouchY === undefined || !touch)
            return;
        const currentY = touch.clientY;
        const d = getDistances();
        if (currentY > lastTouchY + 2)
            onScrollUp(d.fromTop);
        else if (currentY < lastTouchY - 2)
            onScrollDown(d.fromBottom);
        lastTouchY = currentY;
    };
    const onTouchEnd = () => {
        lastTouchY = undefined;
    };
    // Catch-all for scroll sources not covered above (scrollbar drag, keyboard).
    // Only fires callbacks when a preceding user-input event set the flag, or
    // when the scroll delta is large enough to rule out layout-induced jitter.
    const onScroll = () => {
        if (!el.scrollHeight)
            return;
        const { scrollHeight, scrollTop, clientHeight } = el;
        const delta = scrollTop - lastScrollTop;
        const d = getDistances();
        const changed = scrollHeight !== lastScrollHeight ||
            (trackViewportResize && clientHeight !== lastClientHeight);
        if (!changed && !shouldIgnoreScroll?.() && Math.abs(delta) > 10) {
            if (delta < -1)
                onScrollUp(d.fromTop);
            else if (delta > 1)
                onScrollDown(d.fromBottom);
        }
        lastScrollTop = scrollTop;
        lastScrollHeight = scrollHeight;
        lastClientHeight = clientHeight;
    };
    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    return () => {
        el.removeEventListener('wheel', onWheel);
        el.removeEventListener('touchstart', onTouchStart);
        el.removeEventListener('touchmove', onTouchMove);
        el.removeEventListener('touchend', onTouchEnd);
        scrollTarget.removeEventListener('scroll', onScroll);
    };
}
/**
 * Observes scroll height changes on a container (or document.body). When the
 * scroll position is within `thresholdPx` px of the bottom, scrolls to the bottom.
 */
export function useAutoscroll(enabled, { element, thresholdPx = DEFAULT_THRESHOLD, behavior } = {}) {
    useLayoutEffect(() => {
        const el = element?.current ?? document.body;
        if (!enabled || !el)
            return;
        const unregister = register(el);
        if (map.get(el) > 1)
            return unregister;
        subscriptions.set(el, subscribe(el, thresholdPx, behavior));
        return () => unregister();
    }, [enabled, thresholdPx, element, behavior]);
}
//# sourceMappingURL=useAutoscroll.js.map